import React, { useState, useEffect } from 'react';
import { message, Form, Input, InputNumber } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import providerService from 'services/providers.service';
import { REGISTRATION_FEE_ID } from 'containers/Provider/Applications/MCCYN/Steps/Discounts';
import Switch from 'components/Switch/Switch';
import AsyncSelect from 'components/Select/AsyncSelect';

const RegistrationFee = ({ application, isEditable = false }) => {
  const [registrationFee, setRegistrationFee] = useState(null);
  const [registrationFeeForm] = Form.useForm();
  const hasRegistrationFee = Form.useWatch('hasRegistrationFee', registrationFeeForm);

  useEffect(() => {
    providerService.getFeeRates(application.id).then((data) => {
      setRegistrationFee(data.find((fee) => fee?.type?.id === REGISTRATION_FEE_ID));
    });
  }, [application.id]);

  const { isEditing: isEditingRegFee, editProps: regFeeEditProps } = useEditCard({
    id: 'registration-fee',
    onEditStart: () =>
      registrationFeeForm.setFieldsValue({
        hasRegistrationFee: !!registrationFee,
        chargedBy: registrationFee?.chargedBy?.id,
        frequency: registrationFee?.frequency?.id,
        amount: registrationFee?.amount,
        description: registrationFee?.description,
      }),
    onSave: async () => {
      await registrationFeeForm.validateFields();
      const values = registrationFeeForm.getFieldsValue();
      if (values.hasRegistrationFee && !registrationFee) {
        const newFee = await providerService.addUpdateFeeRate(application.id, {
          amount: values.amount,
          chargedBy: values.chargedBy,
          frequency: values.frequency,
          description: values.description,
          type: {
            id: REGISTRATION_FEE_ID,
            sequenceNumber: 1,
            title: 'Registration Fee',
            description: 'Registration Fee',
          },
          application: { id: application.id },
        });
        setRegistrationFee(newFee);
      } else if (values.hasRegistrationFee && registrationFee) {
        const updatedFee = await providerService.addUpdateFeeRate(application.id, {
          ...registrationFee,
          amount: values.amount,
          chargedBy: values.chargedBy,
          frequency: values.frequency,
          description: values.description,
          application: { id: application.id },
          type: {
            id: REGISTRATION_FEE_ID,
            sequenceNumber: 1,
            title: 'Registration Fee',
            description: 'Registration Fee',
          },
        });
        setRegistrationFee(updatedFee);
      } else if (!values.hasRegistrationFee && registrationFee) {
        await providerService.deleteFeeRate(application.id, registrationFee.id);
        setRegistrationFee(null);
      }
      registrationFeeForm.resetFields();
      message.success('Registration fee updated');
    },
    onDiscard: () => registrationFeeForm.resetFields(),
  });

  return (
    <Card
      title="Registration Fee"
      extra={isEditable ? <CardEdit {...regFeeEditProps} /> : null}
      noBodyPadding
      stickyHeader
    >
      <Form form={registrationFeeForm} disabled={regFeeEditProps.loading}>
        <TabularList
          indented
          data={[
            {
              title:
                'Do you charge a registration fee that does not include the cost of deposits, supplies, activities, transportation, etc?',
              value: isEditingRegFee ? (
                <Form.Item name="hasRegistrationFee" className="mb-0" valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              ) : registrationFee ? (
                'Yes'
              ) : (
                'No'
              ),
              titleRequired: true,
            },
            ...((registrationFee && !isEditingRegFee) || (isEditingRegFee && hasRegistrationFee)
              ? [
                  {
                    title: 'Charged per',
                    value: isEditingRegFee ? (
                      <Form.Item
                        name="chargedBy"
                        className="mb-0"
                        rules={[{ required: true, message: 'Charged per is required' }]}
                      >
                        <AsyncSelect
                          optionsApiUrl="/options/36"
                          getOptionLabel={(option) => option.description}
                          getOptionValue={(option) => option.id}
                          isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                        />
                      </Form.Item>
                    ) : (
                      registrationFee?.chargedBy?.description
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Frequency',
                    value: isEditingRegFee ? (
                      <Form.Item
                        name="frequency"
                        className="mb-0"
                        rules={[{ required: true, message: 'Frequency is required' }]}
                      >
                        <AsyncSelect
                          optionsApiUrl="/options/35"
                          getOptionLabel={(option) => option.description}
                          getOptionValue={(option) => option.id}
                          isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                        />
                      </Form.Item>
                    ) : (
                      registrationFee?.frequency?.description
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Amount',
                    value: isEditingRegFee ? (
                      <Form.Item
                        name="amount"
                        className="mb-0"
                        rules={[{ required: true, message: 'Amount is required' }]}
                      >
                        <InputNumber min={0} prefix="$" className="max-w-72" />
                      </Form.Item>
                    ) : (
                      `$${registrationFee?.amount}`
                    ),
                    titleRequired: true,
                  },
                  {
                    title: 'Additional comments',
                    value: isEditingRegFee ? (
                      <Form.Item
                        name="description"
                        className="mb-0"
                        rules={[{ max: 225, message: 'Description is too long' }]}
                      >
                        <Input.TextArea rows={3} className="max-w-2xl" />
                      </Form.Item>
                    ) : (
                      registrationFee?.description
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Form>
    </Card>
  );
};

export default RegistrationFee;
